import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import {
  Form, useTranslate, required,
} from 'react-admin';
import { type ExpiryTimeGranularity, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import { useFormState, useWatch } from 'react-hook-form';
import { ExpiryDateDisplay } from './ExpiryDateDisplay';
import { CertificationDocumentInput } from './inputs/CertificationDocumentInput';
import { CertificationUserTable } from './CertificationUserTable';
import { type CertificationDocument, type DocumentAssignee, hasCertificationDocument } from '../../../utils/certificationDocuments';
import {
  buildDefaultCertificationDetails,
  type CertificationsByAssetId,
} from '../../../utils/certificationUserForm';

type RenewNowStartDateKey = 'lastInspectionDate' | 'lastReceivedOn';

type RenewNowSingleFormValuesBase = {
  expiryDate: string;
  document?: CertificationDocument;
};

export type RenewNowFormValues =
  | ({ lastInspectionDate: string } & RenewNowSingleFormValuesBase)
  | ({ lastReceivedOn: string } & RenewNowSingleFormValuesBase)
  | { certificationsByAssetId: CertificationsByAssetId };

const RenewNowDialogActions = (props: {
  onBack?: () => void;
  onClose: () => void;
  requiresDocumentaryProof?: boolean;
  isBulk: boolean;
  recipients: DocumentAssignee[];
}) => {

  const translate = useTranslate();
  const { isSubmitting } = useFormState();

  const certificationsByAssetId: CertificationsByAssetId | undefined = useWatch({
    name: 'certificationsByAssetId',
  });

  const document: CertificationDocument | undefined = useWatch({ name: 'document' });

  const documentsReady = !props.requiresDocumentaryProof || (
    props.isBulk
      ? props.recipients.every((r) => hasCertificationDocument(certificationsByAssetId?.[r.id]?.document))
      : hasCertificationDocument(document));

  return (
    <DialogActions>
      {props.onBack && (
        <Button onClick={props.onBack} disabled={isSubmitting}>
          {translate('ra.action.back')}
        </Button>
      )}
      <Button onClick={props.onClose} disabled={isSubmitting}>
        {translate('ra.action.cancel')}
      </Button>
      <Button
        type="submit"
        form="renew-now-form"
        variant="contained"
        disabled={isSubmitting || !documentsReady}
        startIcon={
          isSubmitting
            ? <CircularProgress color="inherit" size={20} />
            : undefined
        }
      >
        {translate('ra.action.confirm')}
      </Button>
    </DialogActions>
  );

};

export const RenewNowModal = (props: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (values: RenewNowFormValues) => Promise<void>;
  timeGranularity?: ExpiryTimeGranularity;
  startDateSource?: RenewNowStartDateKey;
  expiryDateSource?: string;
  startDateLabel: string;
  expiryDateLabel: string;
  helperText: string;
  title: string;
  hasDocumentaryProofField?: boolean;
  bulkRecipients?: DocumentAssignee[];
  formSessionKey?: string | number;
}) => {

  const translate = useTranslate();
  const startDateSource = props.startDateSource ?? 'lastInspectionDate';
  const expiryDateSource = props.expiryDateSource ?? 'expiryDate';

  const isBulkCertificationFlow = props.bulkRecipients !== undefined
    && startDateSource === 'lastReceivedOn';

  const today = moment().format('YYYY-MM-DD');
  const expiry = props.timeGranularity
    ? moment().add(props.timeGranularity.value, props.timeGranularity.type).format('YYYY-MM-DD')
    : today;

  const defaultValues = isBulkCertificationFlow
    ? {
      certificationsByAssetId: buildDefaultCertificationDetails(
        props.bulkRecipients?.map(({ id }) => id) ?? [],
        today,
        expiry,
        false,
      ),
    }
    : {
      [startDateSource]: today,
      [expiryDateSource]: expiry,
    };

  const handleSubmit = async (values: Record<string, unknown>) => {

    if (isBulkCertificationFlow) {

      await props.onSubmit({
        certificationsByAssetId: values.certificationsByAssetId as CertificationsByAssetId,
      });
      return;

    }

    const formValues: RenewNowFormValues = startDateSource === 'lastReceivedOn'
      ? {
        lastReceivedOn: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as CertificationDocument | undefined,
      }
      : {
        lastInspectionDate: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as CertificationDocument | undefined,
      };

    await props.onSubmit(formValues);

  };

  return (
    <Dialog
      open={props.open}
      keepMounted={Boolean(props.onBack)}
      maxWidth={isBulkCertificationFlow ? 'lg' : 'md'}
      fullWidth
    >
      <Form
        id="renew-now-form"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        key={props.formSessionKey ?? 'renew-now'}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {translate(props.title)}
          <IconButton
            onClick={props.onClose}
            aria-label={translate('general.text.close')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {isBulkCertificationFlow ? (
              <CertificationUserTable
                recipients={props.bulkRecipients ?? []}
                timeGranularity={props.timeGranularity ?? { value: 1, type: ExpiryTimeGranularityType.Year }}
                includeFirstIssuedOn={false}
                requiresDocument={Boolean(props.hasDocumentaryProofField)}
              />
            ) : (
              <>
                <ExpiryDateDisplay
                  startDateSource={startDateSource}
                  expiryDateSource={expiryDateSource}
                  timeGranularity={props.timeGranularity}
                  startDateLabel={props.startDateLabel}
                  expiryDateLabel={props.expiryDateLabel}
                  helperText={props.helperText}
                  usage="certifications"
                />
                {props.hasDocumentaryProofField && (
                  <CertificationDocumentInput
                    source="document"
                    label={false}
                    validate={required()}
                    labelSingle={translate(
                      'resources.assets.certifications.text.drop_document_to_upload',
                    )}
                    accept={{
                      'application/pdf': ['.pdf'],
                      'image/*': ['.jpg', '.jpeg', '.png'],
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <RenewNowDialogActions
          onBack={props.onBack}
          onClose={props.onClose}
          requiresDocumentaryProof={Boolean(props.hasDocumentaryProofField)} isBulk={isBulkCertificationFlow}
          recipients={props.bulkRecipients ?? []} />
      </Form>
    </Dialog>
  );

};
